/**
 * 🟥 V18.0 — Premium Gate para el Genio / Lyon IA.
 *
 * Fuente de verdad ÚNICA para bloquear acciones avanzadas (modificación
 * del proyecto, recetas, style packs, imports, layouts, generación,
 * automatizaciones) cuando el usuario está en Plan Gratis.
 *
 * INSTRUCCIONES OFICIALES (Marcelo, feb-2026):
 *  - El chat conversacional NUNCA se bloquea (Gratis + Premium).
 *  - Cualquier acción que MODIFIQUE la app requiere Premium.
 *  - Mensaje estándar EXACTO: "Uso exclusivo Plan Premium".
 *  - Sin variaciones, sin agregar nada más.
 *  - Sin bypass: el gate debe verificarse antes de ejecutar.
 *
 * Detección de Premium (alineada con Header.jsx):
 *  1. localStorage.isPremium === 'true'    (set por /auth/me)
 *  2. isCreator (admin whitelist)          (vía isOwnerAdminEmail)
 *  3. isOwnerPremiumEmail(email)           (whitelist permanente)
 */

import { isOwnerPremiumEmail } from '../utils/isOwnerPremium';
import { isOwnerAdminEmail } from '../utils/isOwnerAdmin';

export const PREMIUM_ONLY_MESSAGE = 'Uso exclusivo Plan Premium';

/**
 * Lee el email del usuario actual desde localStorage. La app guarda
 * `userEmail` al login; usamos esa misma key para coherencia.
 */
function getCurrentEmail() {
  try { return localStorage.getItem('userEmail') || ''; }
  catch { return ''; }
}

/**
 * Detección sincrónica del plan Premium.
 * No hace requests: usa localStorage + whitelist en memoria.
 */
export function isUserPremium() {
  try {
    const flag = localStorage.getItem('isPremium') === 'true';
    const email = getCurrentEmail();
    return flag || isOwnerAdminEmail(email) || isOwnerPremiumEmail(email);
  } catch {
    return false;
  }
}

/**
 * Muestra el mensaje estándar de bloqueo Premium.
 *
 * 🟥 Cuadro Negro Genio Restore (feb-2026, rev2): el bloqueo ahora se
 * presenta como un MODAL ("Esta función está disponible solo para
 * usuarios Premium" + botón "Actualizar a Premium") en lugar de un toast.
 * El modal vive en GenioPremiumGateModal y se monta dentro de
 * LyonHeaderButton, que escucha el evento global `genio-premium-gate`.
 *
 * Como fallback (si por algún motivo el modal no estuviera montado, por
 * ejemplo en una página fuera del Header) seguimos mostrando el toast
 * legacy con el mensaje original "Uso exclusivo Plan Premium".
 */
export function showPremiumGateMessage() {
  // 🟥 Cuadro Negro Genio Restore (feb-2026, rev2): el modal es el único
  // punto de salida. Dejamos de mostrar el toast legacy para evitar
  // duplicación visual (modal + toast).
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('genio-premium-gate'));
    }
  } catch { /* ignore */ }
}

/**
 * Wrapper genérico: ejecuta `action()` SOLO si el usuario es Premium.
 * Caso contrario muestra el mensaje estándar y devuelve `false`.
 *
 * Uso:
 *   onClick={() => withPremiumGate(() => doAdvancedAction())}
 *
 * O para envolver un handler existente:
 *   const safe = wrapWithPremiumGate(originalHandler);
 *
 * @returns {boolean} true si se ejecutó, false si fue bloqueado.
 */
export function withPremiumGate(action) {
  if (isUserPremium()) {
    if (typeof action === 'function') action();
    return true;
  }
  showPremiumGateMessage();
  return false;
}

/**
 * Wrapper que devuelve una NUEVA función con el gate ya aplicado.
 * Útil para inyectar en props de componentes hijos (e.g. LyonHeaderToolbar).
 */
export function wrapWithPremiumGate(originalFn) {
  return (...args) => {
    if (isUserPremium()) {
      return typeof originalFn === 'function' ? originalFn(...args) : undefined;
    }
    showPremiumGateMessage();
    return undefined;
  };
}

const api = {
  PREMIUM_ONLY_MESSAGE,
  isUserPremium,
  showPremiumGateMessage,
  withPremiumGate,
  wrapWithPremiumGate,
};

export default api;
